import { LocationData, PolygonGeometry, MarketReportMarkerEntry, MarketReportMarkerProps } from "./types";

type MarkerColor = {
  bdr: string;
  bck: string;
  copy: string;
};

const MARKER_COLORS: Record<string, MarkerColor> = {
  active: {
    bdr: "63D689",
    bck: "CAF0D6",
    copy: "177335",
  },
  pending: {
    bdr: "FAB957",
    bck: "FAE4C3",
    copy: "AD6714",
  },
  sold: {
    bdr: "FA5D57",
    bck: "FAC5C3",
    copy: "A81E19",
  },
  off_market: {
    bdr: "C2C2C2",
    bck: "EBEBEB",
    copy: "666666",
  },
};

const DEFAULT_MARKER_COLOR = MARKER_COLORS.off_market;

const compactNumberFormatter = new Intl.NumberFormat("en-US", {
  notation: "compact",
  maximumFractionDigits: 1,
});

export const getMarketReportMarkers = (
  locationData: LocationData | null,
  isDrawing: boolean,
  geoJSON: PolygonGeometry | null,
): MarketReportMarkerEntry[] => {
  const listingInfo = locationData?.listing_info;

  if ((isDrawing && !geoJSON) || !listingInfo) {
    return [];
  }

  const keyCounts = new Map<string, number>();

  return Object.entries(listingInfo).flatMap(([type, markers]) =>
    markers.map((marker) => {
      const baseId = `${type}-${marker.lat}-${marker.lng}-${marker.price ?? "na"}`;
      const duplicateCount = keyCounts.get(baseId) ?? 0;

      keyCounts.set(baseId, duplicateCount + 1);

      return {
        id: `${baseId}-${duplicateCount}`,
        marker,
        type,
      };
    }),
  );
};

const MarketReportMarker = ({ marker, type }: MarketReportMarkerProps) => {
  const color = MARKER_COLORS[type] || DEFAULT_MARKER_COLOR;
  const label = `$${compactNumberFormatter.format(marker?.price ?? 0)}`;

  return (
    <div
      aria-label={`${type} listing marker`}
      className="tw-pointer-events-none tw-relative tw-flex tw-justify-center -tw-translate-x-1/2 -tw-translate-y-full"
    >
      <div
        className="tw-relative tw-flex tw-min-w-[58px] tw-items-center tw-justify-center tw-rounded-full tw-border-solid tw-border-[1.5px] tw-px-2 tw-py-[3px] tw-shadow-[0_2px_6px_rgba(0,0,0,0.18)]"
        style={{
          borderColor: `#${color.bdr}`,
          backgroundColor: `#${color.bck}`,
        }}
      >
        <span
          className="tw-text-sm tw-font-semibold tw-leading-[1.2] tw-tracking-[0.05em]"
          style={{ color: `#${color.copy}` }}
        >
          {label}
        </span>
        <div
          className="tw-absolute tw-left-1/2 tw-top-full tw-h-0 tw-w-0 -tw-translate-x-1/2"
          style={{
            borderLeft: "7px solid transparent",
            borderRight: "7px solid transparent",
            borderTop: `7px solid #${color.bdr}`,
          }}
        >
          <div
            className="tw-absolute tw-left-1/2 tw-top-[-7px] tw-h-0 tw-w-0 -tw-translate-x-1/2"
            style={{
              borderLeft: "6px solid transparent",
              borderRight: "6px solid transparent",
              borderTop: `6px solid #${color.bck}`,
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default MarketReportMarker;
